import { useNavigate } from "flemo";
import { LockKeyholeIcon } from "lucide-react";
import { motion } from "motion/react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { WordSet } from "@/types/models";

type CategoryCardProps = {
  wordSet: WordSet;
  knownCount?: number;
  learningCount?: number;
  isLocked?: boolean;
  onLockedClick?: () => void;
  onCompleted: () => void;
};

export function CategoryCard({
  wordSet,
  knownCount = 0,
  learningCount = 0,
  isLocked = false,
  onLockedClick,
  onCompleted,
}: CategoryCardProps) {
  const { push } = useNavigate();

  const total = wordSet.word_count;
  const knownPercent = total > 0 ? (knownCount / total) * 100 : 0;
  const learningPercent = total > 0 ? (learningCount / total) * 100 : 0;

  return (
    <Card
      className={cn(
        "relative overflow-hidden rounded-lg text-left transition-transform bg-white pt-4 pb-5",
        isLocked ? "cursor-default" : "active:scale-[0.98] cursor-pointer",
      )}
      onClick={
        isLocked
          ? onLockedClick
          : total > 0 && knownCount === total
            ? onCompleted
            : () => push("/flashcards/:id", { id: wordSet.id })
      }
    >
      {/* 학습 카드 수 */}
      <div className="absolute top-3 right-0 text-xs text-muted-foreground font-medium bg-muted rounded-l px-3 py-1 z-10">
        {knownCount + learningCount}/{wordSet.word_count}
      </div>

      <CardContent className="flex flex-col gap-5.5 px-5">
        <div className="flex items-center">
          {/* 세트 이름 */}
          <span className="font-semibold text-foreground text-base">{wordSet.title}</span>

          {/* 무료/유료 표시 */}
          {!wordSet.is_free && (
            <Badge
              variant="outline"
              className="ml-2 bg-primary/15 text-primary border-none px-2 py-0.5 text-xs font-semibold rounded-md"
            >
              Plus
            </Badge>
          )}
        </div>

        {/* 진행 바 (알고있음: emerald, 학습중: orange) */}
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden flex">
          <motion.div
            className="h-full shrink-0 bg-emerald-500"
            initial={false}
            animate={{ width: `${knownPercent}%` }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          />
          <motion.div
            className="h-full shrink-0 bg-orange-400"
            initial={false}
            animate={{ width: `${learningPercent}%` }}
            transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
          />
        </div>
      </CardContent>

      {/* 잠금 딤 오버레이 */}
      {isLocked && (
        <div className="absolute inset-0 z-10 bg-muted-foreground/25 rounded-lg flex items-center justify-center">
          <LockKeyholeIcon className="size-5 text-zinc-700" />
        </div>
      )}
    </Card>
  );
}
